<template lang="pug">
.story-publications.panel.panel-default
  .panel-heading
    template(v-if="!multiplePublications")
      .row.only
        .col-md-6
          h4 {{ publication.name }}
          |
          |
          small publication
        .col-md-6.text-right
          ShowHideButton.btn-sm(v-model="shown")

    template(v-else)
      label.multiple
        CheckboxIcon.check-icon(v-if="!updating" :checked="checked" @click.native="toggle")
        span.loader-wrapper(v-if="updating")
          Loader.loader
        h4 {{ publication.name }}
          |
          |
          small publication

  .panel-body(v-show="checkedOrShown")
    .form-group
      .flex-between
        label.control-label Story template
        HelpPopover(title="Story template" :content="this.webTemplateHelp")
      CommunityqStoryTemplateField(
        :publication-id="publication.id"
        v-model="storyPublication.story_template_id"
        v-if="!publication.hide_story_templates"
      )
      select.form-control(v-else disabled)
        option(selected) disabled for this publication

    .form-group
      CommunityqSubsitesField(
        :publication-id="publication.id"
        v-model="storyPublication.subsite_ids"
      )

    .form-group
      .flex-between
        label.control-label Content type
        HelpPopover(title="Content type" :content="this.webContentTypeHelp")
      CommunityqContentTypeField(
        :publication-id="publication.id"
        v-model="storyPublication.content_type_key"
        v-if="!publication.hide_content_types"
      )
      select.form-control(v-else disabled)
        option(selected) disabled for this publication
</template>

<script>
import { mapStores, mapState } from 'pinia'
import { usePublicationsStore } from '@/stores/publications'

import CheckboxIcon from '@/components/shared/checkbox_icon.vue'
import CommunityqContentTypeField from './communityq_content_type_field.vue'
import CommunityqStoryTemplateField from './communityq_story_template_field.vue'
import CommunityqSubsitesField from './communityq_subsites_field.vue'
import HelpPopover from '@/components/shared/help_popover.vue'
import Loader from '@/components/shared/loader.vue'
import ShowHideButton from '@/components/show_hide_button.vue'

const webTemplateHelp = `These are templates that can be applied to your story to more appropriately display it online. One layout emphasizes a map. Others create larger or smaller photo displays. One emphasizes a pull quote.

Some of these require additional steps in the web CMS to deploy, like the one shouldering a sidebar, which has to be linked to it in the web CMS.`

const webContentTypeHelp = `The website uses a variety of “content types” to organize material. Various content types have different fields, many of which aren't available in newsroomQ.

The system will assume you are sending a story to the website, but it is possible you will send a press release or a different content type.`

export default {
  setup() {
      return { webTemplateHelp, webContentTypeHelp }
  },

  components: {
    CheckboxIcon,
    CommunityqContentTypeField,
    CommunityqStoryTemplateField,
    CommunityqSubsitesField,
    HelpPopover,
    Loader,
    ShowHideButton
  },

  props: {
    storyId: {
      type: Number,
      required: true
    },

    publication: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      shown: false,
      updating: false
    }
  },

  computed: {
    ...mapStores(usePublicationsStore),
    ...mapState(usePublicationsStore, { multiplePublications: 'hasMultiple' }),

    storyPublication() {
      return this.$store.getters['storyPublications/byPublicationId'](this.publication.id);
    },

    multiplePublications() {
      return this.publicationsStore.hasMultiple
    },

    checked() {
      return !!this.storyPublication.id
    },

    checkedOrShown() {
      return this.multiplePublications ? this.checked : this.shown;
    }
  },

  watch: {
    storyPublication(storyPub) {
      // Check here in case the async call to fetch story publications loads after mounting
      this.syncState(storyPub)
    }
  },

  mounted() {
    // Check here in case the async call to fetch story publications loads before mounting
    this.syncState(this.storyPublication);
  },

  methods: {
    syncState(storyPub) {
      // TODO: make sure this works
      // TODO: determine if we need to check subsites here
      if(!this.multiplePublications && (storyPub.content_type_key || storyPub.story_template_id)) {
        this.shown = true;
      }
    },

    toggle() {
      const { id } = this.storyPublication

      this.updating = true

      if (id) {
        const story_publication = { id, story_id: this.storyId }

        this.$store.dispatch('storyPublications/destroy', { story_publication }).then(() => {
          this.fetchStoryPubs()
        })
      } else {
        const story_publication = { ...this.storyPublication, story_id: this.storyId }

        this.$store.dispatch('storyPublications/create', { story_publication }).then(() => {
          this.fetchStoryPubs()
        })
      }
    },

    fetchStoryPubs() {
      const { storyId } = this

      this.$store.dispatch('storyPublications/fetch', { storyId }).then(() => {
        this.updating = false
      })
    }
  }
}
</script>

<style scoped>
  .panel-heading label {
    position: relative;
    display: block;
  }

  .check-icon {
    position: relative;
    display: inline-block;
    margin: 0 0.5em 0 0;
  }

  h4 {
    position: relative;
    display: inline-block;
    vertical-align: middle;
  }

  h4 small {
    padding-left: 0.5em;
  }

  .loader-wrapper {
    display: inline-block;
    height: 1em;
    width: 1em;
    margin: 0 0.5em 0 0;
  }

  .loader {
    width: 1em;
  }

  .row.only h4 {
    margin-top: 7px;
  }
</style>
